import i18n from 'src/components/lib/i18n/new-i18n';
import * as React from 'react';

import type {Dispatch, ThunkSyncAction, ThunkAction} from 'src/reducers';
import type {ApiError} from 'src/utils/redux-api-v2';
import type {ModalComponent, ModalRender} from 'src/types/modal';
import type {ModalType} from 'src/components/modals/modal-root';

import {captureApiError} from 'src/utils/errors';


export const PUSH_MODAL = 'modal/pushModal';
export const REPLACE_MODAL = 'modal/replaceModal';
export const POP_MODAL = 'modal/popModal';
export const SET_ERROR_MODAL = 'modal/setErrorModal';
export const REMOVE_ERROR_MODAL = 'modal/removeErrorModal';
export const PUSH_SIDEPANEL = 'modal/pushSidepanel';

type Base = {
  className?: string;
};

export type Modal = Base & {
  type: ModalType | ModalComponent;
  render?: undefined;
  text?: string | React.ReactNode;
  allowClickAway?: boolean;
  children?: React.ReactNode;
  confirmText?: string;
  confirmStyle?: 'primary' | 'destructive' | 'default';
  abortText?: string;
  errorText?: string;
  showError?: boolean;
  handleConfirm?: (event: React.SyntheticEvent) => unknown;
  handleAbort?: (event: React.SyntheticEvent) => unknown;
  removeModal?: () => void;
  topmost?: boolean;
  modalVersion?: 'genesis';
};

export type Sidepanel = Modal & {
  title?: React.ReactNode;
  width?: string;
  direction: 'left' | 'right';
  showClear?: boolean;
};

export type ConfirmModal = Base & {
  text?: React.ReactNode;
  title?: string;
  details?: string;
};

export type TitleBarDetailsModal = Modal & {
  title?: string;
  details?: string | React.ReactNode;
  titleIcon?: React.ReactNode;
  titleBarColor?: string;
  hasTitleXButton?: boolean;
  removeModal?: () => void;
  titleElement?: React.ReactNode;
  cancelIcon?: React.ReactNode;
};

export type BasicModal =
  | Modal
  | {
      render: ModalRender;
      allowClickAway?: boolean;
    };

export type PushModalAction = {
  type: 'modal/pushModal';
  payload: BasicModal;
};

export type ReplaceModalAction = {
  type: 'modal/replaceModal';
  payload: BasicModal;
};

export type PopModalAction = {
  type: 'modal/popModal';
};

export type SetErrorModalAction = {
  type: 'modal/setErrorModal';
  payload: Modal;
};

export type RemoveErrorModalAction = {
  type: 'modal/removeErrorModal';
};

export type PushSidepanelAction = {
  type: 'modal/pushSidepanel';
  payload: Sidepanel;
};

export type ModalAction =
  | PushModalAction
  | ReplaceModalAction
  | PopModalAction
  | SetErrorModalAction
  | RemoveErrorModalAction
  | PushSidepanelAction;

export const pushModal = (payload: BasicModal): PushModalAction => ({
  type: PUSH_MODAL,
  payload,
});

export const replaceModal = (payload: BasicModal): ReplaceModalAction => ({
  type: REPLACE_MODAL,
  payload,
});

export const popModal = (..._args: undefined[]): PopModalAction => ({
  type: POP_MODAL,
});

export const setErrorModal = (
  payload: TitleBarDetailsModal,
): SetErrorModalAction => ({
  type: SET_ERROR_MODAL,
  payload,
});

export const removeErrorModal = (): RemoveErrorModalAction => ({
  type: REMOVE_ERROR_MODAL,
});

const setSidepanel = (payload: Sidepanel): PushSidepanelAction => ({
  type: PUSH_SIDEPANEL,
  payload,
});

export const pushSidepanel =
  ({...specificOptions}: Partial<Sidepanel>): ThunkSyncAction =>
  (dispatch: Dispatch) => {
    const defaultOptions: Sidepanel = {
      type: 'GENERIC_SIDEPANEL',
      direction: 'right',
    };
    dispatch(
      setSidepanel({
        ...defaultOptions,
        ...specificOptions,
      }),
    );
  };

export const showGenericError =
  ({...specificOptions}: Partial<TitleBarDetailsModal>): ThunkSyncAction =>
  (dispatch: Dispatch) => {
    const defaultOptions = {
      type: 'GENERIC_ERROR',
      title: 'Error',
      confirmText: 'Okay',
      removeModal: () => {
        dispatch(removeErrorModal());
      },
    } as const;
    dispatch(
      setErrorModal({
        ...defaultOptions,
        ...specificOptions,
      }),
    );
  };

export const showGenericModal =
  (
    modalType: ModalType,
    extras?: Partial<TitleBarDetailsModal>,
  ): ((dispatch: Dispatch) => Promise<boolean>) =>
  (dispatch: Dispatch): Promise<boolean> =>
    new Promise((resolve: (result: Promise<boolean> | boolean) => void) => {
      dispatch(
        pushModal({
          type: modalType,
          confirmText: 'Okay',
          handleConfirm: () => {
            dispatch(popModal());
            resolve(true);
          },
          ...extras,
        }),
      );
    });

export const showGenericAlert = (
  extras?: Partial<TitleBarDetailsModal>,
): ThunkAction<boolean> => showGenericModal('GENERIC_MODAL', extras);

export const showGenericSuccess = (
  extras?: Partial<TitleBarDetailsModal>,
): ThunkAction<boolean> => showGenericModal('GENERIC_SUCCESS', extras);

export const showGenericConfirm =
  (specificOptions: Partial<TitleBarDetailsModal> = {}): ThunkAction<boolean> =>
  (dispatch: Dispatch): Promise<boolean> =>
    new Promise((resolve: (result: Promise<boolean> | boolean) => void) => {
      const defaultOptions: Partial<TitleBarDetailsModal> = {
        type: 'GENERIC_MODAL',
        text: 'Are you sure?',
        confirmText: 'Confirm',
        handleConfirm: () => {
          resolve(true);
        },
        abortText: i18n.t('Cancel', 'Cancel'),
        handleAbort: () => {
          resolve(false);
        },
        removeModal: () => {
          dispatch(popModal());
        },
        allowClickAway: false,
      };
      dispatch(
        // @ts-expect-error - TS2345 - Argument of type '{ className?: string; type?: ModalType | ModalComponent; render?: undefined; text?: string | React.ReactNode; allowClickAway?: boolean; children?: React.ReactNode; confirmText?: string; ... 15 more ...; cancelIcon?: React.ReactNode; }' is not assignable to parameter of type 'BasicModal'.
        pushModal({
          ...defaultOptions,
          ...specificOptions,
        }),
      );
    });

// Dispatch a modal that resolves with a custom value T
export function pushResolvingModal<T>(
  specificOptions: Partial<Modal>,
): ThunkAction<T> {
  return (dispatch: Dispatch): Promise<T> =>
    new Promise((resolve, reject: (error?: any) => void) => {
      dispatch(
        pushModal({
          ...specificOptions,
          handleConfirm: (e) => {
            // @ts-expect-error - TS2345 - Argument of type 'SyntheticEvent<Element, Event>' is not assignable to parameter of type 'T | PromiseLike<T>'.
            resolve(e);
            dispatch(popModal());
          },
          handleAbort: () => {
            dispatch(popModal());
            reject();
          },
          removeModal: () => {
            dispatch(popModal());
            reject();
          },
          allowClickAway: true,
          // @ts-expect-error - TS2353 - Object literal may only specify known properties, and '_resolve' does not exist in type 'Modal'.
          _resolve: resolve,
          _rej: reject,
        }),
      );
    });
}

// TODO (kyle): this is a stopgap until Flow supports ErrorEvent
interface ErrorEvent {
  message: string;
  filename: string;
  lineno: number;
  colno: number;
  stack: string;
  toString(): string;
}

export const showGenericRuntimeError =
  (error: ErrorEvent): ThunkSyncAction =>
  (dispatch: Dispatch) => {
    // @ts-expect-error - TS2551 - Property 'senseErrorEvent' does not exist on type 'Window & typeof globalThis'. Did you mean 'ErrorEvent'?
    window.senseErrorEvent = error;

    let details;
    if (error.message.toLowerCase().includes('script error')) {
      details = 'See console.';
    } else {
      details = `${error.message}
${error.filename} @ ${error.lineno} | ${error.colno}
${error.stack || error.toString()}`;
    }

    dispatch(
      showGenericError({
        type: 'GENERIC_ERROR',
        title: 'Unexpected Error',
        text: 'Apologies, but an unexpected error occured. If this keeps happening, please email support@sensehq.com',
        details,
      }),
    );
  };

export const showApiError =
  (apiError: Partial<ApiError>, text: string): ThunkSyncAction =>
  (dispatch: Dispatch) =>
    dispatch(
      showGenericError({
        type: 'GENERIC_ERROR',
        title: 'API Error',
        text,
        details: `${
          apiError.error ? apiError.error.toString() : ''
        }\n${JSON.stringify(apiError.response, null, 2)}`,
      }),
    );

export const showHandledApiError = ({
  title = 'API Error',
  text,
  json,
  details,
  error,
  confirmText,
}: {
  title?: string;
  text: string;
  json?: unknown;
  details?: string;
  error?: ApiError;
  confirmText?: string;
}): ThunkSyncAction => {
  const finalJson = json || error?.responseBody;
  return showGenericError({
    title,
    text,
    details:
      details ||
      (finalJson ? JSON.stringify(finalJson, null, '  ') : undefined),
    confirmText,
  });
};

export const showAndCaptureApiError = (
  error: ApiError,
  options: {
    title?: string;
    text: string;
    confirmText?: string;
  },
): ThunkSyncAction => {
  captureApiError(error);
  return showHandledApiError({
    ...options,
    json: error.responseBody,
  });
};

export const showUnhandledApiError = (
  error: ApiError,
  errorId: string,
  extras?: Record<any, any>,
): ThunkSyncAction =>
  showGenericError({
    title: 'API Error',
    text: 'An unexpected API error occurred. If this keeps happening, please email the following info to support@sensehq.com',
    details: `${String(error.error)}
Status: ${error.response.status}
Sentry: ${errorId}

Request:
${JSON.stringify(error.options, null, 2)}

Response:
${JSON.stringify(error.response, null, 2)}

Response Body:
${String(JSON.stringify(error.responseBody ?? null, null, 2))}`,
    ...extras,
  });

export const pushEditUserModal =
  (accountId?: string): ThunkSyncAction =>
  (dispatch: Dispatch) => {
    dispatch(
      pushModal({
        type: 'CREATE_USER_MODAL',
        // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'accountId' does not exist in type 'BasicModal'.
        accountId,
      }),
    );
  };

export const showCsvComingSoon = (): ThunkAction<boolean> =>
  showGenericAlert({
    text: 'To export your CSV, please remove the filters. CSV Exports with filtering options will be available soon.',
  });
